import React from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Flex,
} from '@chakra-ui/react';
import { FaUsers, FaUserPlus, FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function SettingsEmployeeManagementFree() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  return (
    <Container maxW="container.xl" py={10} position="relative">
      {/* Lock Overlay */}
      <Flex
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={5}
        bg="rgba(255, 255, 255, 0.92)"
        backdropFilter="blur(3px)"
        borderRadius="xl"
        justify="center"
        align="center"
        flexDirection="column"
        textAlign="center"
        border="3px dashed"
        borderColor="brand.teal"
      >
        <Icon as={FaLock} boxSize={16} color="brand.navy" mb={4} />
        <Heading size="xl" mb={3} color="brand.navy">
          Role Management & Creation
        </Heading>
        <Text color="gray.700" maxW="md" mb={6} fontSize="lg" fontWeight="medium">
          Unlock advanced employee management, role controls, and productivity insights with FinanSee Premium.
        </Text>
        <Button
          size="lg"
          colorScheme="teal"
          bgGradient="linear(to-r, brand.navy, brand.tealDark)"
          onClick={() => navigate('/subscription')}
          mb={3}
          px={8}
          py={6}
          fontSize="lg"
        >
          Upgrade Now
        </Button>
        <Button variant="ghost" colorScheme="teal" onClick={onOpen} size="md">
          What's Included?
        </Button>
      </Flex>

      {/* Blurred SettingsPaid Scaffolding - Much More Visible */}
      <Box filter="blur(1.5px)" opacity={0.6} pointerEvents="none">
        <VStack spacing={10} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Heading
              size="xl"
              bgGradient="linear(to-r, brand.navy, brand.tealDark)"
              bgClip="text"
              mb={2}
            >
              Employee Management
            </Heading>
            <Text color="gray.600" fontSize="md">
              Create and manage Relationship Managers for your organization
            </Text>
          </Box>

          {/* Action Button Section */}
          <Flex
            justify="space-between"
            align="center"
            bgGradient="linear(to-r, brand.navy, brand.tealDark)"
            color="white"
            p={6}
            borderRadius="xl"
            boxShadow="xl"
          >
            <HStack spacing={3}>
              <Icon as={FaUsers} boxSize={7} />
              <Box>
                <Text fontSize="sm" opacity={0.9}>
                  Total Employees
                </Text>
                <Heading size="lg">3</Heading>
              </Box>
            </HStack>

            <Button
              leftIcon={<FaUserPlus />}
              variant="solid"
              colorScheme="blackAlpha"
              size="lg"
              color="white"
            >
              Add Relationship Manager
            </Button>
          </Flex>

          {/* Employee List Section */}
          <Card borderRadius="2xl" boxShadow="xl" overflow="hidden">
            <CardBody p={0}>
              <Box
                bgGradient="linear(to-r, brand.navy, brand.tealDark)"
                color="white"
                p={6}
              >
                <Heading size="md">
                  <HStack spacing={2}>
                    <Icon as={FaUsers} />
                    <Text>Role Directory</Text>
                  </HStack>
                </Heading>
              </Box>

              <Box p={6}>
                <VStack spacing={4} align="stretch">
                  {/* Sample Employee Rows */}
                  <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="md">
                    <Box>
                      <Text fontWeight="bold" fontSize="md">John Smith</Text>
                      <Text fontSize="sm" color="gray.600">john.smith@company.com</Text>
                    </Box>
                    <Box
                      px={4}
                      py={1}
                      bg="brand.navy"
                      color="white"
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      RM
                    </Box>
                  </Flex>

                  <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="md">
                    <Box>
                      <Text fontWeight="bold" fontSize="md">Sarah Johnson</Text>
                      <Text fontSize="sm" color="gray.600">sarah.j@company.com</Text>
                    </Box>
                    <Box
                      px={4}
                      py={1}
                      bg="brand.navy"
                      color="white"
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      RM
                    </Box>
                  </Flex>

                  <Flex justify="space-between" align="center" p={4} bg="gray.50" borderRadius="md">
                    <Box>
                      <Text fontWeight="bold" fontSize="md">Michael Chen</Text>
                      <Text fontSize="sm" color="gray.600">m.chen@company.com</Text>
                    </Box>
                    <Box
                      px={4}
                      py={1}
                      bg="brand.navy"
                      color="white"
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      RM
                    </Box>
                  </Flex>
                </VStack>
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      {/* Popup Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay backdropFilter="blur(8px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FaLock} color="brand.tealDark" />
              <Text>What's Included in FinanSee Premium</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={4}>
              <Text>Add and manage unlimited employees (Relationship Managers)</Text>
              <Text>Role-based permissions and secure access controls</Text>
              <Text>Full, unlimited performance and productivity insights</Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="teal"
              bgGradient="linear(to-r, brand.navy, brand.tealDark)"
              onClick={() => {
                onClose();
                navigate('/subscription');
              }}
            >
              Upgrade to Premium
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default SettingsEmployeeManagementFree;